import { useEffect, useState } from "react";

import { BusinessOption } from "../types/webstoreAnalytics";
import { BusinessPicker } from "./BusinessPicker";
import { DateRangePreset } from "../utils/dateRanges";

interface WebstoreFiltersProps {
  preset: DateRangePreset;
  startDate: string;
  endDate: string;
  onPresetChange: (preset: DateRangePreset) => void;
  onCustomDatesChange: (startDate: string, endDate: string) => void;
  businessId: string;
  onBusinessChange: (businessId: string) => void;
  businesses: BusinessOption[];
  businessesLoading: boolean;
}

const PRESETS = [
  { id: "last-7", label: "Last 7 Days" },
  { id: "last-30", label: "Last 30 Days" },
  { id: "this-month", label: "This Month" },
  { id: "prev-month", label: "Prev Month" },
  { id: "custom", label: "Custom" },
] as const;

export function WebstoreFilters({
  preset,
  startDate,
  endDate,
  onPresetChange,
  onCustomDatesChange,
  businessId,
  onBusinessChange,
  businesses,
  businessesLoading,
}: WebstoreFiltersProps) {
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);
  const [error, setError] = useState<string | null>(null);

  // Keep the custom inputs showing the range actually in effect when a preset moves it.
  useEffect(() => {
    setTempStart(startDate);
    setTempEnd(endDate);
  }, [startDate, endDate]);

  const handleApplyCustom = (e: React.FormEvent) => {
    e.preventDefault();
    if (!tempStart || !tempEnd) {
      setError("Please select both start and end dates.");
      return;
    }
    if (new Date(tempEnd) < new Date(tempStart)) {
      setError("End date cannot be before start date.");
      return;
    }
    setError(null);
    onCustomDatesChange(tempStart, tempEnd);
  };

  return (
    <div className="flex flex-col gap-3 bg-white border border-border rounded-card p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2">
          Date Range:
        </span>
        <div className="inline-flex rounded-md shadow-sm">
          {PRESETS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                onPresetChange(item.id);
                setError(null);
              }}
              className={`px-3 py-1.5 text-xs font-medium border-y border-r first:border-l first:rounded-l-md last:rounded-r-md transition-colors ${
                preset === item.id
                  ? "bg-accent-blue text-white border-accent-blue"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {preset === "custom" && (
          <form onSubmit={handleApplyCustom} className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={tempStart}
              onChange={(e) => setTempStart(e.target.value)}
              className="bg-white border border-slate-200 rounded px-2.5 py-1 text-xs text-slate-700 focus:outline-none focus:border-accent-blue"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date"
              value={tempEnd}
              onChange={(e) => setTempEnd(e.target.value)}
              className="bg-white border border-slate-200 rounded px-2.5 py-1 text-xs text-slate-700 focus:outline-none focus:border-accent-blue"
            />
            <button
              type="submit"
              className="bg-slate-800 text-white hover:bg-slate-700 px-3 py-1 rounded text-xs font-medium transition-colors border border-slate-800"
            >
              Apply
            </button>
            {error && <span className="text-[11px] text-accent-red font-medium">{error}</span>}
          </form>
        )}
      </div>

      <BusinessPicker
        businessId={businessId}
        onChange={onBusinessChange}
        businesses={businesses}
        isLoading={businessesLoading}
      />
    </div>
  );
}
